// A IDENTIDADE DE UM LAYOUT, igual em todo lugar (FR-036).
//
// Hub, ficha, painel da unidade, lista de prefeituras, trocas de arquivos e
// relatorios mostravam o mesmo layout de quatro jeitos. Aqui e' um so', sempre
// na mesma ordem: nome · codigo · versao · situacao. Quem le aprende uma vez.

import { Link } from "react-router-dom";
import { Pill, type PillVariant } from "@atlas/ui/web";
import type { LayoutSituacao } from "@atlas/sdk";

/**
 * O padrão Atlas, pelo código.
 *
 * A web não importa `@atlas/domain` (é da API — ADR-0007), e vários registros
 * guardam só o código do layout: o relatório de conversão, a linha do histórico
 * de trocas. Sem esta lista o chip chamaria de "adaptado" justamente o modelo
 * interno, que é o oposto do que ele é. São dois códigos estáveis — o padrão
 * não entra e sai do catálogo como um layout de ente.
 */
const CODIGOS_PADRAO = new Set(["atlas-retorno", "atlas-movimento"]);

export const ehCodigoPadrao = (codigo: string): boolean => CODIGOS_PADRAO.has(codigo.trim());

export interface LayoutChipProps {
  nome: string;
  codigo: string;
  versao: string;
  situacao?: LayoutSituacao;
  /** `true` = e' o padrao Atlas (a pilula diz "padrão", nao a situacao). */
  padrao?: boolean;
  /** Rascunho atribuido a unidade de prova: a pilula diz "rascunho em teste". */
  emTeste?: boolean;
  tamanho?: "sm" | "md";
  /** Vira link para a ficha. */
  comLink?: boolean;
}

function pilula(p: LayoutChipProps): { variant: PillVariant; texto: string } {
  if (p.padrao) return { variant: "expirado", texto: "padrão" };
  switch (p.situacao) {
    case "rascunho":
      return { variant: "pendente", texto: p.emTeste ? "rascunho em teste" : "rascunho" };
    case "descontinuado":
      return { variant: "rejeitada", texto: "descontinuado" };
    case "teste":
      return { variant: "expirado", texto: "teste" };
    default:
      return { variant: "emdia", texto: "adaptado" };
  }
}

export function LayoutChip(props: LayoutChipProps) {
  const { nome, codigo, versao, tamanho = "md", comLink = false } = props;
  const p = pilula(props);
  const fonte = tamanho === "sm" ? 12 : 14;
  // QUEM NAO TEM O NOME cai no codigo — e' o caso do relatorio guardado, que
  // grava so' codigo e versao. Repetir a mesma palavra duas vezes seguidas nao
  // e' consistencia, e' ruido: nesse caso o codigo aparece uma vez so'.
  const repetido = nome.trim() === codigo.trim();
  const conteudo = (
    <span
      data-layout-chip={codigo}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        fontSize: fonte,
        lineHeight: 1.3,
      }}
    >
      {repetido ? null : <strong style={{ color: "var(--text)" }}>{nome}</strong>}
      <code style={{ fontFamily: "var(--font-mono)", fontSize: fonte - 1, color: "var(--text-muted)" }}>
        {codigo}
      </code>
      <span style={{ color: "var(--text-muted)", fontSize: fonte - 1 }}>v{versao}</span>
      <Pill variant={p.variant}>{p.texto}</Pill>
    </span>
  );
  if (!comLink) return conteudo;
  return (
    <Link
      to={`/averbadora/layouts/${encodeURIComponent(codigo)}`}
      style={{ textDecoration: "none", color: "inherit" }}
      aria-label={`Abrir a ficha do layout ${nome}`}
    >
      {conteudo}
    </Link>
  );
}
